import React, { useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faWandMagicSparkles } from "@fortawesome/pro-solid-svg-icons";
import { faBlockBrick } from "@fortawesome/pro-regular-svg-icons";

import Card from "@/components/card/index.tsx";
import CheckBox from "@/components/checkbox";
import ButtonLockDependent from "@/components/buttons/button-lock-dependent";
import useViewerItemsVisibilityContext from "@/services/context/viewer/items-visibility/use-viewer-items-visibility-context";
import useStoreyIndicatorsContext from "../context/indicators/use-storey-indicators-context";
import useProjectContext from "@/services/context/project/use-project-context";
import useDashboardProcess from "@/hooks/use-dashboard-process";
import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";
import FlowMode from "@/models/enums/flow-mode";

interface MeasureCard3DViewerActionsProps {
    onLaunchPlanDetection: () => void;
}

const MeasureCard3DViewerActions: React.FC<MeasureCard3DViewerActionsProps> = ({
    onLaunchPlanDetection,
}) => {

    const { project } = useProjectContext();
    const { isLoading } = useViewerContext();
    const { dashboardProcessStatusQuery } = useDashboardProcess(project.id);

    const { storeyIndicatorsData } = useStoreyIndicatorsContext();
    const { addItems, removeItems } = useViewerItemsVisibilityContext();

    const indicators = useMemo(() => storeyIndicatorsData, [storeyIndicatorsData]);

    const onIndicatorCheckChanged = useCallback((indicatorKeyname: string, checked: boolean) => {
        if (indicators == null) return;

        const filteredIndicators = indicators.filter(i => i.def.keyName === indicatorKeyname);
        const ids = filteredIndicators?.map(i => i._3D);

        if (ids == null) return;
        if (checked) {
            addItems(ids);
        } else {
            removeItems(ids);
        }
    }, [indicators, addItems, removeItems]);

    return (
        <div className='absolute right-4 inset-y-4 z-1 pointer-events-none flex flex-col gap-y-4 ml-auto'>
            {(project.flowMode === FlowMode._2D3D || project.flowMode === FlowMode._2D3DAdvanced) &&
            <Card additionalContentClasses='flex flex-col gap-y-2 px-4 py-2 text-sm'>
                <ButtonLockDependent
                    variant='simple'
                    size='sm'
                    onClick={onLaunchPlanDetection}
                    disabled={isLoading}
                >
                    <div className='flex flex-row gap-x-2 items-center'>
                        <div className='flex flex-row gap-x-1 items-baseline'>
                            <FontAwesomeIcon icon={faBlockBrick} />
                            <FontAwesomeIcon icon={faWandMagicSparkles} />

                            {dashboardProcessStatusQuery.data?.hasPreparationCoques &&
                                <FontAwesomeIcon icon={faCheck} className='text-green-500' />
                            }
                        </div>
                        <span>Détection automatique</span>
                    </div>
                </ButtonLockDependent>

                <div>
                    <span>Obtenez automatiquement</span>
                    <ul className='list-disc list-inside pl-2 text-sm'>
                        <li>Mur</li>
                        <li>Porte</li>
                        <li>Fenêtre</li>
                    </ul>
                </div>
            </Card>
            }

            {project.flowMode === FlowMode._3D2D &&
            <Card additionalContentClasses='flex flex-col gap-y-2 px-4 py-2 text-sm'>
                <div>BIM</div>

                <div className='grid grid-cols-[1fr_auto] gap-y-2 gap-x-2 items-center text-sm w-fit'>
                    {dashboardProcessStatusQuery.data == null || !dashboardProcessStatusQuery.data?.hasMetresCarbone ? (
                        <ul className='list-disc list-inside pl-2 text-sm'>
                            <li>Façades nettes</li>
                        </ul>
                    ) : (
                        <>
                        <CheckBox
                            disabled={isLoading}
                            onChange={(e) => onIndicatorCheckChanged('SFN', e.target.checked)}
                            checkedColors='checked:bg-[#9accff] checked:border-[#9accff]' />
                        <div>Façades nettes</div>
                        </>
                    )}

                    {dashboardProcessStatusQuery.data == null || !dashboardProcessStatusQuery.data?.hasMetresCarbone ? (
                        <ul className='list-disc list-inside pl-2 text-sm'>
                            <li>Murs gros-œuvre nets</li>
                        </ul>
                    ) : (
                        <>
                        <CheckBox
                            disabled={isLoading}
                            onChange={(e) => onIndicatorCheckChanged('MIN', e.target.checked)}
                            checkedColors='checked:bg-[#cdcdcd] checked:border-[#cdcdcd]' />
                        <div>Murs gros-œuvre nets</div>
                        </>
                    )}

                    {dashboardProcessStatusQuery.data == null || !dashboardProcessStatusQuery.data?.hasQuantitatifLotMenuiseries ? (
                        <ul className='list-disc list-inside pl-2 text-sm'>
                            <li>Portes (Intérieures Gros-Œuvre)</li>
                        </ul>
                    ) : (
                        <>
                        {/* <CheckBox
                            disabled={isLoading}
                            onChange={(e) => onIndicatorCheckChanged('Joinery0', e.target.checked)}
                            checkedColors='checked:bg-[#e5780b] checked:border-[#e5780b]' />
                        <div>Portes (Intérieures Gros-Œuvre)</div> */}
                        </>
                    )}

                    {dashboardProcessStatusQuery.data == null || !dashboardProcessStatusQuery.data?.hasQuantitatifLotMenuiseries ? (
                        <ul className='list-disc list-inside pl-2 text-sm'>
                            <li>Portes (Intérieures Appartement)</li>
                        </ul>
                    ) : (
                        <>
                        {/* <CheckBox
                            disabled={isLoading}
                            onChange={(e) => onIndicatorCheckChanged('Joinery2', e.target.checked)}
                            checkedColors='checked:bg-[#90ee90] checked:border-[#90ee90]' />
                        <div>Portes (Intérieures Appartement)</div> */}
                        </>
                    )}

                    {dashboardProcessStatusQuery.data == null || !dashboardProcessStatusQuery.data?.hasQuantitatifLotMenuiseries ? (
                        <ul className='list-disc list-inside pl-2 text-sm'>
                            <li>Portes (Restantes)</li>
                        </ul>
                    ) : (
                        <>
                        {/* <CheckBox
                            disabled={isLoading}
                            onChange={(e) => onIndicatorCheckChanged('Joinery3', e.target.checked)}
                            checkedColors='checked:bg-[#1e1e72] checked:border-[#1e1e72]' />
                        <div>Portes (Restantes)</div> */}
                        </>
                    )}

                    {dashboardProcessStatusQuery.data == null || !dashboardProcessStatusQuery.data?.hasQuantitatifLotMex ? (
                        <ul className='list-disc list-inside pl-2 text-sm'>
                            <li>Fenêtres</li>
                        </ul>
                    ) : (
                        <>
                        {/* <CheckBox
                            disabled={isLoading}
                            onChange={(e) => onIndicatorCheckChanged('Joinery1', e.target.checked)}
                            checkedColors='checked:bg-[#66b3ff] checked:border-[#66b3ff]' />
                        <div>Fenêtres</div> */}
                        </>
                    )}
                </div>
            </Card>
            }
        </div>
    );
};

export default MeasureCard3DViewerActions;